// src/components/chat/PatientChatInput.tsx
// Cursor-style chat input with buttons on separate row

import AttachFileIcon from "@mui/icons-material/AttachFile";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import MicIcon from "@mui/icons-material/Mic";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import {
  alpha,
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useRef } from "react";
import { useTranslation } from "../../i18n";

interface PatientChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onFileUpload: (files: File[]) => void;
  onVoiceRecord: () => void;
  onCameraCapture: () => void;
  disabled?: boolean;
  recording?: boolean;
  processing?: boolean;
  processingMessage?: string;
  placeholder?: string;
}

const PatientChatInput: React.FC<PatientChatInputProps> = ({
  value,
  onChange,
  onSend,
  onFileUpload,
  onVoiceRecord,
  onCameraCapture,
  disabled = false,
  recording = false,
  processing = false,
  processingMessage,
  placeholder,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Use translation for default placeholder
  const inputPlaceholder = placeholder || t("chat.type_message", "Type a message...");

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !disabled && !processing) {
        onSend();
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      onFileUpload(files);
    }
    e.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      onFileUpload(files);
    }
  };

  const isDisabled = disabled || processing;

  return (
    <Box
      data-tour="upload-documents"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        pt: 1,
        px: 0, // No horizontal padding - inherit from parent
        pb: 0, // No bottom padding
        backgroundColor: "transparent", // Blend with parent card
      }}
    >
      <Paper
        elevation={0}
        sx={{
          backgroundColor: theme.palette.action.hover,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          transition: "border-color 0.2s",
          "&:focus-within": {
            borderColor: theme.palette.primary.main,
          },
        }}
      >
        {/* When processing (transcribing), replace the input with a centered spinner */}
        {processing ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 110,
              gap: 1,
            }}
          >
            <CircularProgress
              size={24}
              thickness={4}
              sx={{ color: theme.palette.primary.main }}
            />
            <Typography
              variant="body2"
              sx={{
                color: theme.custom?.contentText?.secondary || theme.palette.text.secondary,
                fontWeight: 500,
                fontSize: "0.85rem",
              }}
            >
              {processingMessage || t("chat.transcribing", "Transcribing audio...")}
            </Typography>
          </Box>
        ) : (
          /* Normal text input */
          <InputBase
            data-tour="chat-input"
            multiline
            minRows={3}
            maxRows={8}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={inputPlaceholder}
            disabled={isDisabled || recording}
            sx={{
              width: "100%",
              px: 2,
              py: 1.5,
              fontSize: "0.95rem",
              lineHeight: 1.6,
              color: theme.custom?.contentText?.primary || theme.palette.text.primary,
              "& .MuiInputBase-input": {
                padding: 0,
                "&::placeholder": {
                  color: theme.custom?.contentText?.muted || theme.palette.text.disabled,
                  opacity: 1,
                },
              },
            }}
          />
        )}

        {/* Buttons Row - hidden during processing */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.5,
            py: 1,
            opacity: processing ? 0.35 : 1,
            pointerEvents: processing ? "none" : "auto",
            transition: "opacity 0.2s",
          }}
        >
          {/* Left side: Action buttons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {/* Voice Record Button — re-enabled 2026-05-28 (cursor_logs/035 B4). */}
            <Tooltip title={recording ? t("chat.stop_recording", "Stop recording") : t("chat.record_voice", "Record voice note")}>
              <IconButton
                onClick={onVoiceRecord}
                disabled={isDisabled}
                size="small"
                sx={{
                  color: recording
                    ? theme.palette.error.main
                    : (theme.custom?.contentText?.secondary || theme.palette.text.secondary),
                  backgroundColor: recording
                    ? alpha(theme.palette.error.main, 0.08)
                    : "transparent",
                  "&:hover": {
                    backgroundColor: recording
                      ? alpha(theme.palette.error.main, 0.15)
                      : theme.palette.action.hover,
                  },
                }}
              >
                {recording ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            {/* File Attach Button */}
            <Tooltip title={t("chat.attach_files", "Attach files")}>
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                disabled={isDisabled || recording}
                size="small"
                sx={{ color: theme.custom?.contentText?.secondary || theme.palette.text.secondary }}
              >
                <AttachFileIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,application/pdf,.doc,.docx,.txt,.xlsx,.xls"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />

            {/* Camera Button */}
            <Tooltip title={t("chat.take_photo", "Take photo")}>
              <IconButton
                onClick={onCameraCapture}
                disabled={isDisabled || recording}
                size="small"
                sx={{ color: theme.custom?.contentText?.secondary || theme.palette.text.secondary }}
              >
                <CameraAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Right side: Send Button */}
          <Tooltip title={t("chat.send_message", "Send message (Enter)")}>
            <span>
              <IconButton
                data-tour="send-message"
                onClick={onSend}
                disabled={isDisabled || recording || !value.trim()}
                size="medium"
                sx={{
                  // When text has been typed the button is enabled: give it a
                  // solid primary fill (white icon) so it clearly reads as
                  // active/clickable. Empty input keeps the muted/disabled look.
                  color: value.trim()
                    ? theme.palette.primary.contrastText
                    : theme.palette.text.disabled,
                  backgroundColor: value.trim()
                    ? theme.palette.primary.main
                    : "transparent",
                  transition: "background-color 0.2s ease, color 0.2s ease",
                  "&:hover": {
                    backgroundColor: value.trim()
                      ? theme.palette.primary.dark
                      : "transparent",
                  },
                  "&.Mui-disabled": {
                    color: theme.palette.text.disabled,
                    backgroundColor: "transparent",
                  },
                }}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Paper>
    </Box>
  );
};

export default PatientChatInput;
